import { GetApiUserInfoApiResponse, PostApiProjectResourceApiArg, userOrg } from './userOrgApi';
import type { FontFamily, PresetColor } from 'types/config';
import { ThemeMode } from 'types/config';

export const injectedUserOrgApi = userOrg.injectEndpoints({
    endpoints: build => ({
        getApiUserInfoWithHeaders: build.query<GetApiUserInfoApiWithHeadersResponse, GetApiUserInfoApiWithHeadersArg>({
            query: () => ({ url: `/api/UserInfo` }),
            transformResponse: (response: GetApiUserInfoApiResponse, meta) => {
                const headers = meta?.response?.headers;
                const headersObj: UserInfoHeaders = {
                    alertmessage: undefined,
                    alertstatus: undefined,
                };
                if (headers && typeof headers.forEach === 'function') {
                    headers.forEach((value, key) => {
                        headersObj[key] = value;
                    });
                }
                headersObj.alertmessage = headersObj.alertmessage ?? undefined;
                headersObj.alertstatus = headersObj.alertstatus ?? undefined;
                return {
                    data: response,
                    headers: headersObj,
                };
            },
        }),
        getApiUserPreferencesParsed: build.query<GetApiUserPreferencesResponse, void>({
            query: () => ({ url: `/api/User/Preferences` }),
            transformResponse: (response: string) => JSON.parse(response),
        }),
        putApiUserPreferences: build.mutation<void, PutApiUserPreferencesArg>({
            query: body => ({
                url: `/api/User/Preferences`,
                method: 'PUT',
                body: body.body,
            }),
        }),
        postApiProjectResource: build.mutation<{ locationHeader?: string }, PostApiProjectResourceApiArg>({
            query: queryArg => ({
                url: `/api/ProjectResource`,
                method: 'POST',
                body: queryArg.projectResourceCreate,
            }),
            transformResponse: (response: any, meta: any) => {
                const locationHeader = meta?.response?.headers.get('Location');
                return { locationHeader: locationHeader || undefined };
            },
        }),
    }),
    overrideExisting: true,
});

export type PaginationOptions = {
    pageIndex: number;
    pageSize: number;
};

export type TableState = {
    pagination?: PaginationOptions;
    searchText?: string;
    columnSearch?: unknown[];
    selectedRowIds?: (string | number)[];
};

type TableName =
    | 'projects'
    | 'customHPCScriptWorkflows'
    | 'workflows'
    | 'uploadDatasets'
    | 'workflowDatasets'
    | 'publicDatasets'
    | 'jobscripts'
    | 'jobscriptVersions'
    | 'locations'
    | 'providers'
    | 'resourceManagement'
    | 'uploadTransferStatuses'
    | 'stageTransferStatuses'
    | 'deleteTransferStatuses';

type AllTableStates = {
    [key in TableName]?: TableState;
};

export type TableOptions = {
    density?: string;
} & AllTableStates;

export type SelectedMetric = { project: string; type: string; chartType: string };

export type PutApiUserPreferencesArg = {
    body: {
        showOnlyMyDatasets?: boolean;
        showOnlyMyExecutions?: boolean;
        globalFilterValues?: string[];
        fontFamily?: FontFamily;
        presetColor?: PresetColor;
        mode?: ThemeMode;
        tableOptions?: TableOptions;
        firstStart?: boolean;
        graphOptions?: {
            direction?: string;
            position?: any;
            [key: string]: any;
        };
        ddiDashboardOptions?: {
            endTime?: string;
            interval?: string;
            selectedMetrics?: SelectedMetric[];
        };
    };
};

export type GetApiUserPreferencesResponse = {
    showOnlyMyDatasets?: boolean;
    showOnlyMyExecutions?: boolean;
    globalFilterValues?: string[];
    fontFamily?: FontFamily;
    presetColor?: PresetColor;
    mode?: ThemeMode;
    tableOptions?: TableOptions;
    firstStart?: boolean;
    graphOptions?: {
        direction?: string;
        position?: any;
        [key: string]: any;
    };
    ddiDashboardOptions?: {
        endTime?: string;
        interval?: string;
        selectedMetrics?: SelectedMetric[];
    };
};

export type GetApiUserInfoApiWithHeadersResponse = UserInfoWithHeadersModel;
export type UserInfoWithHeadersModel = { data: GetApiUserInfoApiResponse; headers: UserInfoHeaders };
export type GetApiUserInfoApiWithHeadersArg = void;
export type UserInfoHeaders = {
    alertmessage: string | undefined;
    alertstatus: 'error' | 'info' | 'success' | 'warning' | undefined;
    [key: string]: string | undefined;
};

export const {
    useGetVersionQuery,
    useGetCommandTemplatePermissionsQuery,
    useGetCommandTemplatesQuery,
    useActivateCommandTemplateMutation,
    useDeactivateCommandTemplateMutation,
    useWorkerDataQuery,
    useGetApiHeappeListChangedFilesForJobByProjectShortNameAndResourceNameSubmittedJobInfoIdQuery,
    useGetApiOtherCountryQuery,
    useGetApiOtherStatisticsQuery,
    useGetApiProjectByProjectShortNameUsersQuery,
    useGetProjectQuery,
    usePostApiProjectMutation,
    usePutApiProjectMutation,
    usePutApiProjectByProjectShortNameUserAndIdpUserIdAssignRoleIdMutation,
    useDeleteApiProjectByProjectShortNameMutation,
    usePutApiProjectByProjectShortNameProjectResourceAndProjectResourceNameMutation,
    useDeleteApiProjectByProjectShortNameProjectResourceAndProjectResourceNameMutation,
    usePutApiProjectByProjectShortNameUserAndIdpUserIdMutation,
    useDeleteApiProjectByProjectShortNameUserAndIdpUserIdMutation,
    usePutApiProjectByProjectShortNameProjectTagAndProjectTagIdMutation,
    useDeleteApiProjectByProjectShortNameProjectTagAndProjectTagIdMutation,
    useTemplatesInfoQuery,
    useExtendedProjectInfoQuery,
    useValidRolesQuery,
    useGetApiProjectResourceAssignmentQuery,
    usePostApiProjectResourceAssignmentMutation,
    usePutApiProjectResourceAssignmentMutation,
    useGetProjectResourceAssignmentQuery,
    useDeleteApiProjectResourceAssignmentByIdMutation,
    useGetApiProjectResourceQuery,
    usePostApiProjectResourceMutation,
    usePutApiProjectResourceMutation,
    useGetProjectResourceQuery,
    useDeleteApiProjectResourceByIdMutation,
    useGetApiProjectTagQuery,
    usePostApiProjectTagMutation,
    usePutApiProjectTagMutation,
    useGetProjectTagQuery,
    useDeleteApiProjectTagByIdMutation,
    usePostApiPublicHandleByActionLinkMutation,
    useGetApiPublicRequestInfoByRequestIdQuery,
    useGetApiRequestsQuery,
    usePostApiRequestsMutation,
    useGetApiRequestsSentRequestsQuery,
    usePutApiRequestsByRequestIdAcceptMutation,
    usePutApiRequestsByRequestIdRejectMutation,
    useSyncDetailsQuery,
    useProjectsInfoQuery,
    useGetApiUserPreferencesQuery,
    usePutApiUserPreferencesMutation,
    useGetApiUserInfoQuery,
    useGetApiUserInfoExtendedQuery,
    useGetApiUserPreferencesParsedQuery,
    useLazyGetApiProjectResourceQuery,
} = injectedUserOrgApi;
